import { useEffect, useMemo, useState } from "react";
import {
	Box,
	Button,
	CircularProgress,
	FormControl,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Switch,
	Typography,
} from "@mui/material";

import {
	fetchData,
	FetchDataFailureAction,
	FetchDataSuccessAction,
} from "../../../store/actions";
import { getVelocityData, jiraChartResponseProfile } from "../../../Service/Apis";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { RootState } from "../../../store/Store";
import { useDispatch, useSelector } from "react-redux";
import MonthButtonGroup from "../../utilization/MonthsButtonGroup";
import { useCustomToast } from "../../Methods/SnackBar";
import LineChartComponent from "../../charts/LineChart";
import { isEmployeeAccessRole } from "../../../utils/accessRole";
import {
	jiraChartBlobDownload,
	normalizeEmployeeName,
	resolveJiraExportEmployeeIds,
} from "../chartExport";

const MONTH_TO_LABEL: Record<string, string> = {
	"1": "1M",
	"3": "3M",
	"6": "6M",
	"12": "1Y",
};

const VelocityChart = () => {
	const dispatch2: ThunkDispatch<
		RootState,
		null,
		FetchDataSuccessAction | FetchDataFailureAction
	> = useDispatch();
	const [velocityData, setVelocityData] = useState<any>([]);
	const [teamName, setTeamName] = useState<string>("");
	const [month, setMonth] = useState<string>("1");
	const [selectedFormat, setSelectedFormat] = useState<string>("excel");
	const showToast = useCustomToast();
	const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
	const [loading, setLoading] = useState(false);
	const [downloading, setDownloading] = useState(false);
	const [selectAll, setSelectAll] = useState(true);

	const nrole = useSelector((state: any) => state.auth.selectedRole);
	const paramsData = useMemo(
		() => ({ ...jiraChartResponseProfile, role: nrole ?? "Employee" }),
		[nrole]
	);

	const fetchVelocityData = async (selectedMonth: string) => {
		try {
			setLoading(true);
			const res = await dispatch2(
				fetchData(`${getVelocityData}${selectedMonth}`, paramsData)
			);
			setMonth(selectedMonth);
			setVelocityData(res);
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchVelocityData("1");
	}, []);

	useEffect(() => {
		if (velocityData?.teams?.length && !teamName) {
			setTeamName(velocityData.teams[0].team_name);
		}
	}, [velocityData, teamName]);

	useEffect(() => {
		if (!velocityData || loading) return;
		if (velocityData.teams?.length) {
			const tn = teamName || velocityData.teams[0]?.team_name;
			const t = velocityData.teams.find(
				(x: any) => x.team_name === tn
			);
			if (!t?.employees?.length) return;
			setSelectedEmployees(
				t.employees.map((e: any) => e.employee_name)
			);
			setSelectAll(true);
		} else if (velocityData.data?.length) {
			const names: string[] = Array.from(
				new Set(
					velocityData.data.flatMap((entry: any) =>
						entry.velocity.map((v: any) => v.assignee_name)
					)
				)
			);
			setSelectedEmployees(names);
			setSelectAll(true);
		}
	}, [velocityData, loading, teamName]);

	const handleMonthChange = (selectedMonth: string) => {
		fetchVelocityData(selectedMonth);
	};

	const buttonData = [
		{ label: "1M", onClick: () => handleMonthChange("1") },
		{ label: "3M", onClick: () => handleMonthChange("3") },
		{ label: "6M", onClick: () => handleMonthChange("6") },
		{ label: "1Y", onClick: () => handleMonthChange("12") },
	];

	const computedData = useMemo(() => {
		if (!velocityData) return [];

		const selectedTeam =
			velocityData?.teams?.find(
				(team: any) => team.team_name === teamName
			) || velocityData?.teams?.[0];

		const nameToId = new Map(
			(selectedTeam?.employees || []).map((e: any) => [
				normalizeEmployeeName(e.employee_name),
				String(e.employee_id),
			])
		);

		if (selectedTeam) {
			return selectedTeam.issues.flatMap((issue: any) =>
				issue.velocity.map((entry: any) => ({
					employee_name: entry.assignee_name,
					employee_id: nameToId.get(
						normalizeEmployeeName(entry.assignee_name)
					),
					week: issue.week_end,
					percent: entry.hours_per_point,
				}))
			);
		}

		return (
			velocityData?.data?.flatMap((issue: any) =>
				issue.velocity.map((entry: any) => ({
					employee_name: entry.assignee_name,
					employee_id: undefined,
					week: issue.week_end,
					percent: entry.hours_per_point,
				}))
			) || []
		);
	}, [velocityData, teamName]);

	const chartEmployeeNames = useMemo((): string[] => {
		return Array.from(
			new Set(
				computedData.map((item: any) => String(item.employee_name))
			)
		);
	}, [computedData]);

	const onDownload = async () => {
		try {
			setDownloading(true);
			const isEmployeeRole =
				(nrole ?? "Employee").toLowerCase() === "employee";

			let selectedTeam: any = null;
			if (!isEmployeeRole) {
				if (!teamName) {
					throw new Error("Please select a team");
				}
				selectedTeam = velocityData.teams?.find(
					(team: any) => team.team_name === teamName
				);
				if (!selectedTeam) {
					throw new Error("Selected team not found");
				}
			}

			const employeeIds = resolveJiraExportEmployeeIds({
				selectedEmployees,
				chartEmployeeNames,
				teamEmployees: selectedTeam?.employees,
				rowsForIdFallback: computedData as any[],
			});

			const baseParams: Record<string, unknown> = {
				...paramsData,
				download: selectedFormat,
			};
			if (employeeIds) {
				baseParams.employee_ids = employeeIds;
			}
			if (!isEmployeeRole && selectedTeam) {
				baseParams.team_id = selectedTeam.team_id;
			}

			const ext =
				selectedFormat === "excel" ? "xlsx" : selectedFormat;
			const filename = !isEmployeeRole
				? `velocity-${teamName.replace(/\s+/g, "-")}.${ext}`
				: `velocity.${ext}`;

			await jiraChartBlobDownload(
				`${getVelocityData}${month}`,
				baseParams,
				filename,
				showToast
			);
		} catch (error: any) {
			showToast(error?.message ?? String(error), "error", true);
		} finally {
			setDownloading(false);
		}
	};

	const handleEmployeeSelection = (employeeName: string) => {
		const newSelectedEmployees = selectedEmployees.includes(
			employeeName
		)
			? selectedEmployees.filter((name) => name !== employeeName)
			: [...selectedEmployees, employeeName];

		setSelectedEmployees(newSelectedEmployees);

		const uniqueEmployees = Array.from(
			new Set(computedData.map((item: any) => item.employee_name))
		);

		if (newSelectedEmployees.length === uniqueEmployees.length) {
			setSelectAll(true);
		} else {
			setSelectAll(false);
		}
	};

	const getAllEmployeeNames = () => {
		if (velocityData?.teams?.length) {
			const t =
				velocityData.teams.find(
					(team: any) => team.team_name === teamName
				) || velocityData.teams[0];
			return (
				t?.employees?.map(
					(employee: any) => employee.employee_name
				) || []
			);
		}
		return Array.from(
			new Set(
				velocityData.data?.flatMap((entry: any) =>
					entry.velocity.map((item: any) => item.assignee_name)
				) || []
			)
		);
	};

	const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const checked = event.target.checked;
		setSelectAll(checked);

		if (checked) {
			setSelectedEmployees(getAllEmployeeNames());
		} else {
			setSelectedEmployees([]);
		}
	};

	return (
		<>
			<Paper sx={{ m: 2, p: 4 }}>
				<Box display="flex" justifyContent="space-between" alignItems="center">
					<Box
						display="flex"
						justifyContent="center"
						alignItems="center"
						flexDirection="column"
						gap={2}
					>
						<Typography variant="h4">Velocity Chart</Typography>
						<MonthButtonGroup
							buttonData={buttonData}
							value={MONTH_TO_LABEL[month] ?? "1M"}
						/>
					</Box>
					<Box
						display="flex"
						justifyContent="center"
						alignItems="center"
						flexDirection="column"
						gap={2}
					>
						{velocityData?.teams && (
							<FormControl
								size="small"
								sx={{ minWidth: 220, width: "100%", maxWidth: 320 }}
							>
								<InputLabel id="velocity-team-select-label">
									Select Team
								</InputLabel>
								<Select
									labelId="velocity-team-select-label"
									id="velocity-team-select"
									value={teamName}
									label="Select Team"
									onChange={(e) => setTeamName(e.target.value)}
								>
									{velocityData?.teams?.map((team: any) => (
										<MenuItem key={team.team_id} value={team.team_name}>
											{team.team_name}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						)}
						<Box
							display="flex"
							justifyContent="center"
							gap={2}
							alignItems="center"
							flexWrap="wrap"
						>
							<FormControl size="small" sx={{ minWidth: 120 }}>
								<InputLabel id="velocity-export-format-label">
									Format
								</InputLabel>
								<Select
									labelId="velocity-export-format-label"
									label="Format"
									value={selectedFormat}
									onChange={(e) =>
										setSelectedFormat(e.target.value)
									}
								>
									<MenuItem value="excel">
										Excel (.xlsx)
									</MenuItem>
									<MenuItem value="csv">CSV</MenuItem>
									<MenuItem value="json">JSON</MenuItem>
								</Select>
							</FormControl>
							{downloading ? (
								<Box
									display="flex"
									justifyContent="center"
									alignItems="center"
									gap={2}
								>
									<CircularProgress size="1rem" />
									<Typography>Downloading...</Typography>
								</Box>
							) : (
								<Button
									variant="outlined"
									size="small"
									onClick={onDownload}
								>
									Export
								</Button>
							)}
						</Box>
					</Box>
				</Box>
				{!isEmployeeAccessRole(nrole) && (
					<Box
						display="flex"
						justifyContent="flex-end"
						alignItems="center"
						gap={2}
						m={2}
						width="100%"
					>
						<Typography>Select All Employees</Typography>
						<Switch
							checked={selectAll}
							onChange={handleSwitchChange}
							color="primary"
						/>
					</Box>
				)}
				{loading ? (
					<Box
						display="flex"
						justifyContent="center"
						alignItems="center"
						width="100%"
						height={200}
					>
						<CircularProgress />
					</Box>
				) : (
					<LineChartComponent
						data={computedData || []}
						YAxisLabel="Time Spent (hours)"
						selectedEmployees={selectedEmployees}
						onEmployeeSelection={handleEmployeeSelection}
					/>
				)}
			</Paper>
		</>
	);
};

export default VelocityChart;
